import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  canEnterModuleAtHotel as canAccessModuleByPermissionAtHotel,
  hasPermissionAtHotel as hasUserPermissionAtHotel,
  isUserDisabled,
  normalizeUserStatus,
} from "./access";
import PermissionPopover from "../components/PermissionPopover";
import { clearSession, getSession, setSession, getValidJwtToken, SESSION_EXPIRED_EVENT, PERMISSION_DENIED_EVENT, type SessionStatus } from "./authSession";
import { getActivePropertyId, setActivePropertyId, subscribeToActiveProperty } from "../properties/propertyContext";
import { ensureIdentityStoreLoaded, getUserById, updateUserLocale, type Locale, type User, type UserStatus } from "./identityStore";
import { resolveEffectiveAccess } from "./effectiveAccess";
import { resolveNavigationContext, type ResolvedNavigationContext } from "./navigation";
import i18n from "../i18n";
import { DEFAULT_UI_LANGUAGE, normalizeUiLanguage } from "../i18n/uiLanguages";
import type { ModuleKey } from "../modules/registry";
import type { AuthenticatedUser, Permission, Role } from "./types";
import { ACTIVE_AUTH_PROVIDER, AUTH_PROVIDER_KEY, getPasswordLoginProvider, isLocalPasswordLoginEnabled, localAuthProvider } from "./providers";
import { setDynamicRolePermissions } from "./roleDefinitionsStore";
import { getApiBaseUrl } from "./apiBase";
import { restoreJwtFromSessionCookie } from "./backendAuth";
import { authDebugLog } from "./authDebug";

type AuthPhase =
  | "booting"
  | "authenticated"
  | "unauthenticated"
  | "error";

type AuthState = {
  status: SessionStatus;
  user: User | null;
  currentUser: User | null;
  hotelId: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isAuthProcessing: boolean;
  authPhase: AuthPhase;
  navigation: ResolvedNavigationContext;
  login: (identifier: string, password: string) => Promise<{ ok: boolean; error?: string; errorKey?: string }>;
  verifyMfa: (code: string) => Promise<boolean>;
  logout: () => void;
  refreshPermissions: () => Promise<void>;
  setLocale: (locale: Locale) => void;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: Role) => boolean;
  canAccessModule: (moduleKey: ModuleKey) => boolean;
};

const Ctx = createContext<AuthState | null>(null);
export { Ctx as AuthCtx };
let authRestorePromise: Promise<User | null> | null = null;
function normalizePreferredLocale(value: string | null | undefined): Locale | null {
  return normalizeUiLanguage(value);
}

function resolveAppLocale(userLocale: Locale): Locale {
  const profileLocale = normalizePreferredLocale(userLocale) ?? DEFAULT_UI_LANGUAGE;
  if (typeof window === "undefined") return profileLocale;
  const stored = normalizePreferredLocale(window.localStorage.getItem("xentros_lang"));
  return stored ?? profileLocale;
}

function logAuthDebug(event: string, details: Record<string, unknown> = {}) {
  authDebugLog("[auth-debug]", {
    area: "auth-context",
    event,
    ...details,
  });
}

function safeAuthLocationHref() {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  if (/^\/book\/groups\/[^/]+/.test(url.pathname)) {
    url.pathname = "/book/groups/[redacted]";
    url.search = "";
    url.hash = "";
  }
  if (/^\/group-rooming-list\/[^/]+/.test(url.pathname)) {
    url.pathname = "/group-rooming-list/[redacted]";
    url.search = "";
    url.hash = "";
  }
  if (/^\/sign\/documents\/[^/]+/.test(url.pathname)) {
    url.pathname = "/sign/documents/[redacted]";
    url.search = "";
    url.hash = "";
  }
  return url.href;
}

function getStatusError(status: UserStatus): string | undefined {
  if (normalizeUserStatus(status) === "inactive") return i18n.t("auth.account_inactive");
  if (isUserDisabled({ status })) return i18n.t("auth.account_disabled");
  return undefined;
}

function persistAuthenticatedUser(
  nextUser: User,
  setUserState: (user: User) => void,
  setStatusState: (status: SessionStatus) => void,
  setHotelIdState: (hotelId: string | null) => void,
) {
  const access = resolveEffectiveAccess(nextUser as AuthenticatedUser);
  const navigation = resolveNavigationContext(nextUser as AuthenticatedUser);
  authDebugLog("[auth] resolved authenticated user", {
    userId: access.userId,
    email: access.email,
    tenantId: access.tenantId,
    assignedRoles: access.assignedRoles,
    permissions: access.permissions,
    properties: navigation.properties.map((property) => property.id),
    visibleModules: navigation.visibleModules.map((module) => module.key),
    defaultLandingPath: navigation.defaultLandingPath,
    reason: access.reason,
  });

  const storedPropertyId = getActivePropertyId(navigation.properties);
  if (!storedPropertyId && navigation.properties.length > 0) {
    setActivePropertyId(navigation.properties[0].id, navigation.properties);
  }
  const hotelId = storedPropertyId ?? navigation.properties[0]?.id ?? null;

  setUserState(nextUser);
  setStatusState("authed");
  setHotelIdState(hotelId);
  setSession({ userId: nextUser.id, status: "authed", hotelId });

  const appLocale = resolveAppLocale(nextUser.locale);
  if ((i18n.resolvedLanguage ?? i18n.language) !== appLocale) {
    i18n.changeLanguage(appLocale);
  }

  if (typeof window !== "undefined") {
    window.localStorage.setItem("xentros_lang", appLocale);
  }
}

async function loadDynamicRoleDefinitions(): Promise<void> {
  try {
    const base = getApiBaseUrl();
    const token = getValidJwtToken();
    // With no token and no stored session the endpoint can only answer 401 —
    // skip the doomed request (static definitions in roles.ts cover the login
    // screen; the post-login call reloads the dynamic ones).
    if (!token && !getSession()) return;
    const response = await fetch(`${base}/api/role-definitions`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) return;
    const data = await response.json() as { roleDefinitions?: Array<{ key: string; permissions: string[] }> };
    if (!Array.isArray(data.roleDefinitions) || data.roleDefinitions.length === 0) return;
    const permsMap: Record<string, string[]> = {};
    for (const def of data.roleDefinitions) {
      if (def.key && Array.isArray(def.permissions)) {
        permsMap[def.key] = def.permissions;
      }
    }
    setDynamicRolePermissions(permsMap);
    authDebugLog("[auth] loaded dynamic role definitions", { count: Object.keys(permsMap).length });
  } catch {
    // Fail silently — static definitions in roles.ts serve as fallback
  }
}

async function restoreConfiguredUser() {
  if (AUTH_PROVIDER_KEY === "local") {
    await ensureIdentityStoreLoaded();
    const localUser = await localAuthProvider.getCurrentUser();
    if (localUser) return localUser;
  }

  return ACTIVE_AUTH_PROVIDER.getCurrentUser();
}

function getRestoreConfiguredUserOnce() {
  if (!authRestorePromise) {
    authRestorePromise = restoreConfiguredUser();
  }

  return authRestorePromise;
}

const PERMISSIONS_POLL_INTERVAL_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("anon");
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthProcessing, setIsAuthProcessing] = useState(false);
  const [authPhase, setAuthPhase] = useState<AuthPhase>("booting");
  const [hotelId, setHotelId] = useState<string | null>(null);
  const [permDeniedMsg, setPermDeniedMsg] = useState<string | null>(null);
  const permDeniedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);

  const refreshPermissions = useCallback(async () => {
    if (isRefreshingRef.current || status !== "authed") return;
    isRefreshingRef.current = true;
    try {
      const refreshed = await ACTIVE_AUTH_PROVIDER.getCurrentUser();
      if (!refreshed) return;
      const statusError = getStatusError(refreshed.status ?? "active");
      if (statusError) return;
      // Role templates are editable in the USM portal — refresh them alongside permissions
      // so template edits reach open sessions without a page reload.
      await loadDynamicRoleDefinitions();
      persistAuthenticatedUser(refreshed, setUser, setStatus, setHotelId);
    } catch (error) {
      console.warn("[auth] background permission refresh failed", error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [status]);

  useEffect(() => {
    let active = true;

    const restore = async () => {
      try {
        authDebugLog("[auth] restore start", {
          href: safeAuthLocationHref(),
        });
        logAuthDebug("restore_start", {
          href: safeAuthLocationHref(),
        });
        // The JWT is memory-only: after a reload, get it back from the session
        // cookie before anything below needs it.
        if (AUTH_PROVIDER_KEY !== "local") await restoreJwtFromSessionCookie();
        // Fetch dynamic role definitions before resolving permissions so that
        // effectiveAccess.ts uses up-to-date role→permission mappings from USM DB.
        await loadDynamicRoleDefinitions();
        const restoredUser = await getRestoreConfiguredUserOnce();
        if (!restoredUser) {
          if (active) {
            authDebugLog("[auth] auth phase", { nextPhase: "unauthenticated" });
            logAuthDebug("auth_phase_transition", { nextPhase: "unauthenticated", reason: "no_restored_user" });
            setAuthPhase("unauthenticated");
          }
          return;
        }

        const statusError = getStatusError(restoredUser.status ?? "active");
        if (statusError) {
          await ACTIVE_AUTH_PROVIDER.logout();
          clearSession();
          authDebugLog("[auth] auth phase", { nextPhase: "unauthenticated" });
          logAuthDebug("auth_phase_transition", { nextPhase: "unauthenticated", reason: "restored_user_status_invalid" });
          setAuthPhase("unauthenticated");
          return;
        }

        if (!active) return;
        persistAuthenticatedUser(restoredUser, setUser, setStatus, setHotelId);
        authDebugLog("[auth] auth phase", { nextPhase: "authenticated" });
        logAuthDebug("auth_phase_transition", { nextPhase: "authenticated", reason: "restore_success", userId: restoredUser.id });
        setAuthPhase("authenticated");
      } catch (error) {
        console.error("Failed to restore authentication state", error);
        if (!active) return;
        clearSession();
        setUser(null);
        setStatus("anon");
        authDebugLog("[auth] auth phase", { nextPhase: "error" });
        logAuthDebug("auth_phase_transition", { nextPhase: "error", reason: "restore_failed", error: error instanceof Error ? error.message : String(error) });
        setAuthPhase("error");
      } finally {
        if (active) {
          setIsInitializing(false);
          setIsAuthProcessing(false);
        }
      }
    };

    void restore();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (AUTH_PROVIDER_KEY !== "local") return undefined;

    const onUsersChanged = () => {
      if (!user) return;
      const refreshed = getUserById(user.id);
      if (!refreshed) return;

      const statusError = getStatusError(refreshed.status ?? "active");
      if (statusError) {
        clearSession();
        setUser(null);
        setStatus("anon");
        return;
      }

      setUser(refreshed);
    };

    window.addEventListener("xentros-identity-users-change", onUsersChanged);
    return () => window.removeEventListener("xentros-identity-users-change", onUsersChanged);
  }, [user]);

  useEffect(() => {
    const onSessionExpired = () => {
      if (status !== "authed") return;
      authDebugLog("[auth] session expired — backend returned 401, logging out");
      clearSession();
      setUser(null);
      setStatus("anon");
      setAuthPhase("unauthenticated");
      setIsAuthProcessing(false);
      void ACTIVE_AUTH_PROVIDER.logout();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [status]);

  useEffect(() => {
    const onPermDenied = (e: Event) => {
      const msg = (e as CustomEvent<{ message?: string }>).detail?.message ?? "Brak uprawnień do tej operacji.";
      setPermDeniedMsg(msg);
      if (permDeniedTimerRef.current) clearTimeout(permDeniedTimerRef.current);
      permDeniedTimerRef.current = setTimeout(() => setPermDeniedMsg(null), 4000);
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, onPermDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onPermDenied);
  }, []);

  useEffect(() => {
    return subscribeToActiveProperty((propertyId) => {
      setHotelId(propertyId);
      const session = getSession();
      if (session) {
        setSession({ ...session, hotelId: propertyId });
      }
    });
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshPermissions();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshPermissions]);

  useEffect(() => {
    const id = setInterval(() => void refreshPermissions(), PERMISSIONS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshPermissions]);

  const api = useMemo<AuthState>(() => ({
    status,
    user,
    currentUser: user,
    hotelId,
    isAuthenticated: status === "authed" && Boolean(user),
    isInitializing,
    isAuthProcessing,
    authPhase,
    navigation: resolveNavigationContext(user as AuthenticatedUser | null, hotelId),

    async login(identifier, password) {
      const provider = isLocalPasswordLoginEnabled() ? getPasswordLoginProvider() : ACTIVE_AUTH_PROVIDER;
      const result = await provider.login({ identifier, password });
      if (!result.ok || !result.user) {
        const errorKey = result.error ?? "auth.invalid";
        const errorMessage = errorKey.startsWith("auth.") ? i18n.t(errorKey) : errorKey;
        return { ok: false, error: errorMessage, errorKey: errorKey.startsWith("auth.") ? errorKey : undefined };
      }

      const statusError = getStatusError(result.user.status ?? "active");
      if (statusError) {
        await ACTIVE_AUTH_PROVIDER.logout();
        authDebugLog("[auth] auth phase", { nextPhase: "error" });
        setAuthPhase("error");
        return { ok: false, error: statusError };
      }

      // The pre-login fetch of role definitions may have run without a token (endpoint
      // requires auth) — reload now that a JWT is available.
      await loadDynamicRoleDefinitions();
      persistAuthenticatedUser(result.user, setUser, setStatus, setHotelId);
      authDebugLog("[auth] auth phase", { nextPhase: "authenticated" });
      setAuthPhase("authenticated");
      return { ok: true };
    },

    async verifyMfa(code) {
      if (!user) return false;
      return code.trim().length > 0;
    },

    refreshPermissions,

    logout() {
      authDebugLog("[auth] logout triggered");
      clearSession();
      setUser(null);
      setStatus("anon");
      setHotelId(null);
      authDebugLog("[auth] auth phase", { nextPhase: "unauthenticated" });
      setAuthPhase("unauthenticated");
      setIsAuthProcessing(false);
      void ACTIVE_AUTH_PROVIDER.logout();
    },

    setLocale(locale: Locale) {
      if (!user || !normalizeUiLanguage(locale)) return;

      if (AUTH_PROVIDER_KEY === "local") {
        updateUserLocale(user.id, locale);
      }

      const updated = { ...user, locale };
      setUser(updated);
      i18n.changeLanguage(locale);
      localStorage.setItem("xentros_lang", locale);
    },

    hasPermission(permission: Permission) {
      return hasUserPermissionAtHotel(user, permission, hotelId);
    },

    hasRole(role: Role) {
      return resolveEffectiveAccess(user as AuthenticatedUser | null).assignedRoles.includes(role);
    },

    canAccessModule(moduleKey: ModuleKey) {
      return canAccessModuleByPermissionAtHotel(user, moduleKey, hotelId);
    },
  }), [authPhase, hotelId, isAuthProcessing, isInitializing, refreshPermissions, status, user]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {permDeniedMsg && (
        <div className="xPermDeniedToast" role="alert" aria-live="assertive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{permDeniedMsg}</span>
        </div>
      )}
      <PermissionPopover />
    </Ctx.Provider>
  );
}

export function useAuth() {
  const value = useContext(Ctx);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
